import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError, setAccessToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setPlan(data.plan);
      // If server tells us we're unauthenticated, drop any stale token
      if (!data.user) setAccessToken(null);
    } catch {
      setUser(null);
      setPlan(null);
      setAccessToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth, AuthCallback exchanges the session_id
    // first — skip the /auth/me probe to avoid a race.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const loginWithGoogleSession = useCallback(async (sessionId, referralCode, repCode) => {
    try {
      const { data } = await api.post("/auth/google/session", {
        session_id: sessionId,
        referral_code: referralCode || null,
        rep_referral_code: repCode || null,
      });
      if (data.access_token) setAccessToken(data.access_token);
      setUser(data.user);
      setPlan(data.plan);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.twofa_required) return { ok: false, twofa: data };
      // Store token for Authorization header fallback (Safari iframe / blocked cookies)
      if (data.access_token) setAccessToken(data.access_token);
      setUser(data.user);
      setPlan(data.plan);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const verifyTwoFactor = async (challengeId, code) => {
    try {
      const { data } = await api.post("/auth/2fa/verify", { challenge_id: challengeId, code });
      if (data.access_token) setAccessToken(data.access_token);
      setUser(data.user);
      setPlan(data.plan);
      return { ok: true, user: data.user, recoveryCodes: data.recovery_codes || null };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data.access_token) setAccessToken(data.access_token);
      setUser(data.user);
      setPlan(data.plan);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setAccessToken(null);
    setUser(null);
    setPlan(null);
  };

  return (
    <AuthContext.Provider value={{ user, plan, loading, login, register, logout, refresh, loginWithGoogleSession, verifyTwoFactor }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside provider");
  return ctx;
}
